import React from 'react';
import { ApolloProvider, useQuery, useMutation } from '@apollo/client';
import { IntlProvider } from 'react-intl';
import gql from 'graphql-tag';
import client from '../lib/apolloClient';
import getMessages from '../lib/i18n/getMessages';
import SupportHelpDrawer from '../components/Succession/SupportHelpDrawer/SupportHelpDrawer';
import { CurrencyProvider } from '../hooks/useCurrency';

const GET_SUPPORT_DRAWER_DATA = gql`
  query GetSupportDrawerData {
    helpArticles {
      id
      title
      description
      md_body
      category
      audience
      source_type
    }
    supportCategories {
      value
      label
      hint
    }
    getMySupport: getMySupportRequests(page: 0, rowsPerPage: 20) {
      id
      friendlyName: friendly_name
      subject
      category
      status
      leadMessage
      createdAt
      user {
        id
      }
      conversation {
        id
        personalMessages {
          id
          body
          sender {
            id
            allowed_name
          }
          documents {
            id
            name
            download_url
            mime_type
          }
          createdAt
        }
      }
    }
  }
`;

const CREATE_SUPPORT_REQUEST = gql`
  mutation CreateSupportRequest($message: String!, $category: String, $subject: String, $files: [File!]) {
    createSupportRequest(message: $message, category: $category, subject: $subject, files: $files) {
      id
      friendlyName: friendly_name
      status
      category
      subject
    }
  }
`;

const CREATE_LEAD_SUPPORT_REQUEST = gql`
  mutation CreateLeadSupportRequest($message: String!, $name: String!, $email: String!, $category: String, $subject: String) {
    createSupportRequestLead(message: $message, name: $name, email: $email, category: $category, subject: $subject) {
      id
      friendlyName: friendly_name
      status
    }
  }
`;

const SEND_SUPPORT_MESSAGE = gql`
  mutation SendSupportMessage($conversationId: ID!, $body: String, $files: [File!]) {
    sendMessageWithAttachments(conversationId: $conversationId, body: $body, files: $files) {
      id
      body
      createdAt
      sender {
        id
        allowed_name
      }
      documents {
        id
        name
        download_url
        mime_type
      }
    }
  }
`;

const GET_MY_SUPPORT_REQUEST = gql`
  query GetMySupportRequest($id: ID!) {
    linkedSupportRequest: getMySupportRequest(id: $id) {
      id
      friendlyName: friendly_name
      subject
      category
      status
      leadMessage
      createdAt
      user {
        id
      }
      conversation {
        id
        personalMessages {
          id
          body
          sender {
            id
            allowed_name
          }
          documents {
            id
            name
            download_url
            mime_type
          }
          createdAt
        }
      }
    }
  }
`;

// Parse ?support_request=<id> from the current URL, returning a string id or null.
const getLinkedSupportRequestId = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('support_request');
    if (!raw || !/^\d+$/.test(raw)) return null;
    return raw;
  } catch (_) {
    return null;
  }
};

const SupportHelpDrawerInner = ({ isAuthenticated, features, currentUser }) => {
  const linkedId = getLinkedSupportRequestId();

  const { data, loading, refetch } = useQuery(GET_SUPPORT_DRAWER_DATA, {
    skip: !isAuthenticated,
    errorPolicy: 'all', // allow partial data even if one field errors
    fetchPolicy: 'cache-and-network',
  });

  const { data: linkedData } = useQuery(GET_MY_SUPPORT_REQUEST, {
    skip: !isAuthenticated || !linkedId,
    variables: { id: linkedId },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  const [createSupportRequest] = useMutation(CREATE_SUPPORT_REQUEST, {
    onCompleted: () => refetch(),
  });

  const [createLeadSupportRequest] = useMutation(CREATE_LEAD_SUPPORT_REQUEST);

  const [sendSupportMessage] = useMutation(SEND_SUPPORT_MESSAGE, {
    onCompleted: () => refetch(),
  });

  const handleDrawerOpen = async () => {
    if (!isAuthenticated) return;
    try {
      await refetch();
    } catch (err) {
      console.error('Failed to refresh support drawer data:', err);
    }
  };

  const handleSubmitRequest = async ({ category, issueDescription, files }) => {
    try {
      const hasFiles = files && files.length > 0;
      await createSupportRequest({
        variables: {
          message: issueDescription,
          category,
          subject: category, // use category as subject if no explicit subject
          files: hasFiles ? files : undefined,
        },
        context: hasFiles ? { hasUpload: true } : {},
      });
      // Ensure data is refreshed even if onCompleted didn't fire
      await refetch();
    } catch (err) {
      console.error('Failed to create support request:', err);
      // Still try to refetch in case the request was partially created
      try { await refetch(); } catch (_) { /* ignore */ }
    }
  };

  const handleSubmitLeadRequest = async ({ category, issueDescription, name, email }) => {
    try {
      await createLeadSupportRequest({
        variables: {
          message: issueDescription,
          name,
          email,
          category,
          subject: category,
        },
      });
    } catch (err) {
      console.error('Failed to create lead support request:', err);
    }
  };

  const handleSendMessage = async ({ conversationId, message, files }) => {
    if (!conversationId || (!message && (!files || files.length === 0))) return;
    const hasFiles = files && files.length > 0;
    await sendSupportMessage({
      variables: {
        conversationId,
        body: message || undefined,
        files: hasFiles ? files : undefined,
      },
      context: hasFiles ? { hasUpload: true } : {},
    });
  };

  const helpArticles = data?.helpArticles || [];
  const supportCategories = data?.supportCategories || [];
  const baseSupportRequests = (data?.getMySupport || []).map((sr) => ({
    ...sr,
    lastMessage: sr.conversation?.personalMessages?.[sr.conversation.personalMessages.length - 1]?.body || sr.leadMessage || '',
  }));

  // Merge the deep-linked request if it resolved and isn't already in the paginated list
  const linkedRequest = linkedData?.linkedSupportRequest || null;
  const supportRequests = (() => {
    if (!linkedRequest) return baseSupportRequests;
    const alreadyPresent = baseSupportRequests.some((r) => String(r.id) === String(linkedRequest.id));
    if (alreadyPresent) return baseSupportRequests;
    const merged = {
      ...linkedRequest,
      lastMessage: linkedRequest.conversation?.personalMessages?.[linkedRequest.conversation.personalMessages.length - 1]?.body || linkedRequest.leadMessage || '',
    };
    return [merged, ...baseSupportRequests];
  })();

  // Count requests with unread replies: open requests where the last message
  // was NOT sent by the current user (i.e. support/AI replied and user hasn't engaged)
  const userId = currentUser?.id?.toString();
  const unreadCount = supportRequests.filter((r) => {
    if (r.status !== 'open') return false;
    const msgs = r.conversation?.personalMessages;
    if (!msgs || msgs.length === 0) return false;
    const lastMsg = msgs[msgs.length - 1];
    return lastMsg.sender?.id?.toString() !== userId;
  }).length;

  // Only pass the deep-link id if the query resolved a valid request for this user
  const initialSelectedRequestId = linkedRequest ? String(linkedRequest.id) : null;

  return (
    <SupportHelpDrawer
      isAuthenticated={isAuthenticated}
      currentUser={currentUser}
      unreadCount={unreadCount}
      helpArticles={helpArticles}
      supportCategories={supportCategories}
      supportRequests={supportRequests}
      initialSelectedRequestId={initialSelectedRequestId}
      onDrawerOpen={handleDrawerOpen}
      onSubmitRequest={handleSubmitRequest}
      onSubmitLeadRequest={handleSubmitLeadRequest}
      onSendMessage={handleSendMessage}
    />
  );
};

const SupportHelpDrawerContainer = (props, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  window.currency = railsContext && railsContext.currency;
  const locale = window.locale;
  return (
    <CurrencyProvider currency={(railsContext && railsContext.currency) || 'USD'}>
      <ApolloProvider client={client}>
        <IntlProvider locale={locale} messages={getMessages(locale)}>
          <SupportHelpDrawerInner {...props} />
        </IntlProvider>
      </ApolloProvider>
    </CurrencyProvider>
  );
};

export default SupportHelpDrawerContainer;
